import { HttpError } from '@refinedev/core';

import axios, { type AxiosInstance } from 'axios';

import { API_AUTH_URL, API_BASE_URL, ENV } from '@/config/env';
import authProvider from '@/providers/authProvider';
import Cookies from 'js-cookie';
import { ApiError } from '@/modules/apps/model/api';
import { getDefaultHeaders } from '../helper/getDefaultHeaders';

interface ApiResponse {
  status_code: number;
  status: string;
  error?: string;
}
// Create Axios instances
const userLogin = Cookies.get('user_login');

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: getDefaultHeaders(userLogin ? JSON.parse(userLogin) : undefined),
});

const axiosAuthInstance = axios.create({
  baseURL: API_AUTH_URL,
});

// Interceptor for overriding request url for mock data server
function applyMockInterceptor(instance: AxiosInstance) {
  instance.interceptors.request.use(
    async (req) => {
      // Should only be used when ENV is mocks
      if (ENV === 'mocks') {
        if (!req.url) return req;

        // Match an origin
        const domPtn = /https?:\/\/[a-zA-Z0-9\-.]+(:[0-9]{3,5})?/g;
        const match = req.url.match(domPtn);

        if (match == null) return req;

        req.url = req.url.replace(match[0], 'http://localhost:4999');
      }
      return req;
    },
  );
}

// Apply the interceptor to both instances
applyMockInterceptor(axiosInstance);
applyMockInterceptor(axiosAuthInstance);

// Response Interceptor for Handling Errors
function applyResponseInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use((response) => response, async (error: ApiError) => {
    const { response, config: originalRequest } = error;
    const errorData: ApiResponse = error.response.data;

    // Validate response object
    if (response.data?.response_status?.error_code != null
      || response.data?.response_status?.error_message != null) {
      const httpError: HttpError = {
        message: response.data?.response_status?.error_message,
        statusCode: response.data?.response_status?.error_code || response.status,
        errors: {
          response,
          request: originalRequest,
          axiosData: error as any,
        },
      };

      return Promise.reject(httpError);
    }

    // Validate HTTP Status Code
    if (response.status >= 400 && response.status !== 401) {
      const httpError: HttpError = {
        message: `Error with status code ${response.status}`,
        statusCode: response.data?.response_status?.error_code || response.status,
        errors: {
          response,
          request: originalRequest,
          axiosData: error as any,
        },
      };

      return Promise.reject(httpError);
    }

    // Handle specific error for status code 400
    if (errorData.status_code === 400) {
      console.error('Error:', errorData?.error || 'An error occurred');
      return Promise.reject(errorData?.error);
    }

    // Handle token refresh for 401 Unauthorized error
    if (errorData.status_code === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.error('Token refresh failed:', errorData?.error);
      authProvider.logout({}); // Make sure this method handles redirection or cleanup
      return Promise.reject(errorData?.error);
    }

    // Add more validation in case of another unhandled error

    // Just return the original axios error data for easier temporary handling
    return Promise.reject(error);
  });
}

// Apply the response interceptor to both instances
applyResponseInterceptor(axiosInstance);
applyResponseInterceptor(axiosAuthInstance);

export { axiosAuthInstance, axiosInstance };
